import React, { useState } from 'react';
import api from '../utils/api';
import VerificationModal from '../components/VerificationModal';
import CategoryManager from '../components/CategoryManager';
import MerchantManager from '../components/MerchantManager';

const Settings: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'profile' | 'security' | 'account' | 'categories' | 'merchants'>('profile');
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [verificationAction, setVerificationAction] = useState<'email' | 'password' | 'delete' | null>(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  // Formular States
  const [newEmail, setNewEmail] = useState('');
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [deleteConfirmation, setDeleteConfirmation] = useState('');

  const user = JSON.parse(localStorage.getItem('user') || '{}');

  // Email ändern - Schritt 1: Code anfordern
  const requestEmailChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      await api.post('/user/change-email/request', { newEmail });
      setVerificationAction('email');
      setShowVerificationModal(true);
      setSuccess('');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Fehler beim Senden des Codes');
    } finally {
      setLoading(false);
    }
  };

  // Email ändern - Schritt 2: Code verifizieren
  const verifyEmailChange = async (code: string) => {
    await api.post('/user/change-email/confirm', { newEmail, code });
    setSuccess('E-Mail-Adresse erfolgreich geändert!');
    setNewEmail('');
    // User-Daten aktualisieren
    const updatedUser = { ...user, email: newEmail };
    localStorage.setItem('user', JSON.stringify(updatedUser));
  };

  // Passwort ändern - Schritt 1: Code anfordern
  const requestPasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (newPassword !== confirmPassword) {
      setError('Passwörter stimmen nicht überein');
      return;
    }

    if (newPassword.length < 6) {
      setError('Passwort muss mindestens 6 Zeichen lang sein');
      return;
    }

    setLoading(true);
    try {
      await api.post('/user/change-password/request');
      setVerificationAction('password');
      setShowVerificationModal(true);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Fehler beim Senden des Codes');
    } finally {
      setLoading(false);
    }
  };

  // Passwort ändern - Schritt 2: Code verifizieren
  const verifyPasswordChange = async (code: string) => {
    await api.post('/user/change-password', { oldPassword, newPassword, code });
    setSuccess('Passwort erfolgreich geändert!');
    setOldPassword('');
    setNewPassword('');
    setConfirmPassword('');
  };

  // Account löschen - Schritt 1: Code anfordern
  const requestAccountDeletion = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (deleteConfirmation !== 'LÖSCHEN') {
      setError('Bitte gib "LÖSCHEN" zur Bestätigung ein');
      return;
    }

    setLoading(true);
    try {
      await api.post('/user/delete-account/request');
      setVerificationAction('delete');
      setShowVerificationModal(true);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Fehler beim Senden des Codes');
    } finally {
      setLoading(false);
    }
  };

  // Account löschen - Schritt 2: Code verifizieren
  const verifyAccountDeletion = async (code: string) => {
    await api.delete('/user/delete-account', { 
      data: { confirmation: deleteConfirmation, code } 
    });
    // Logout und Redirect
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    window.location.href = '/login';
  };

  const handleVerification = async (code: string) => {
    if (verificationAction === 'email') {
      await verifyEmailChange(code);
    } else if (verificationAction === 'password') {
      await verifyPasswordChange(code);
    } else if (verificationAction === 'delete') {
      await verifyAccountDeletion(code);
    }
  };

  return (
    <div className="container" style={{ maxWidth: '800px', marginTop: '40px' }}>
      <h1 style={{ marginBottom: '30px' }}>⚙️ Einstellungen</h1>

      {/* Tabs */}
      <div style={{
        display: 'flex',
        gap: window.innerWidth <= 768 ? '5px' : '10px',
        marginBottom: '30px',
        borderBottom: '2px solid #e9ecef',
        overflowX: window.innerWidth <= 768 ? 'auto' : 'visible',
        WebkitOverflowScrolling: 'touch',
        scrollbarWidth: 'none',
        msOverflowStyle: 'none'
      }}>
        {[
          { key: 'profile', label: '👤 Profil' },
          { key: 'security', label: '🔒 Sicherheit' },
          { key: 'categories', label: '📂 Kategorien' },
          { key: 'merchants', label: '🏪 Händler' },
          { key: 'account', label: '⚠️ Account' }
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => {
              setActiveTab(tab.key as any);
              setError('');
              setSuccess('');
            }}
            style={{
              padding: window.innerWidth <= 768 ? '10px 12px' : '12px 24px',
              border: 'none',
              background: 'none',
              borderBottom: activeTab === tab.key ? '3px solid #667eea' : '3px solid transparent',
              color: activeTab === tab.key ? '#667eea' : '#666',
              fontWeight: activeTab === tab.key ? 'bold' : 'normal',
              cursor: 'pointer',
              fontSize: window.innerWidth <= 768 ? '14px' : '16px',
              whiteSpace: 'nowrap',
              flexShrink: 0
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="card">
        {error && <div className="error">{error}</div>}
        {success && <div className="success" style={{
          padding: '12px',
          background: '#d4edda',
          color: '#155724',
          border: '1px solid #c3e6cb',
          borderRadius: '8px',
          marginBottom: '20px'
        }}>{success}</div>}

        {/* Profil Tab */}
        {activeTab === 'profile' && (
          <div>
            <h2>E-Mail-Adresse ändern</h2>
            <p style={{ color: '#666', marginBottom: '20px' }}>
              Aktuelle E-Mail: <strong>{user.email}</strong>
            </p>

            <form onSubmit={requestEmailChange}>
              <div className="form-group">
                <label>Neue E-Mail-Adresse</label>
                <input
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  placeholder="neue@email.de"
                  required
                  disabled={loading}
                />
              </div>

              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? 'Sende Code...' : 'E-Mail ändern'}
              </button>
            </form>
          </div>
        )}

        {/* Sicherheit Tab */}
        {activeTab === 'security' && (
          <div>
            <h2>Passwort ändern</h2>

            <form onSubmit={requestPasswordChange}>
              <div className="form-group">
                <label>Aktuelles Passwort</label>
                <input
                  type="password"
                  value={oldPassword}
                  onChange={(e) => setOldPassword(e.target.value)}
                  required
                  disabled={loading}
                />
              </div>

              <div className="form-group">
                <label>Neues Passwort</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  minLength={6}
                  required
                  disabled={loading}
                />
              </div>

              <div className="form-group">
                <label>Neues Passwort bestätigen</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  minLength={6}
                  required
                  disabled={loading}
                />
              </div>

              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? 'Sende Code...' : 'Passwort ändern'}
              </button>
            </form>
          </div>
        )}

        {/* Account Tab */}
        {activeTab === 'account' && (
          <div>
            <h2>⚠️ Account löschen</h2>
            <div style={{
              background: '#fff3cd',
              border: '1px solid #ffc107',
              borderRadius: '8px',
              padding: '16px',
              marginBottom: '20px'
            }}>
              <strong>⚠️ Warnung:</strong> Diese Aktion kann nicht rückgängig gemacht werden!
              <br />
              Alle deine Daten (Konten, Transaktionen, Schulden) werden permanent gelöscht.
            </div>

            <form onSubmit={requestAccountDeletion}>
              <div className="form-group">
                <label>Gib "LÖSCHEN" ein zur Bestätigung</label>
                <input
                  type="text"
                  value={deleteConfirmation}
                  onChange={(e) => setDeleteConfirmation(e.target.value)}
                  placeholder="LÖSCHEN"
                  required
                  disabled={loading}
                  style={{ fontFamily: 'monospace', fontSize: '18px' }}
                />
              </div>

              <button 
                type="submit" 
                className="btn" 
                disabled={loading || deleteConfirmation !== 'LÖSCHEN'}
                style={{
                  background: '#dc3545',
                  color: 'white',
                  border: 'none'
                }}
              >
                {loading ? 'Sende Code...' : 'Account unwiderruflich löschen'}
              </button>
            </form>
          </div>
        )}

        {/* Kategorien Tab */}
        {activeTab === 'categories' && <CategoryManager />}

        {/* Händler Tab */}
        {activeTab === 'merchants' && <MerchantManager />}
      </div>

      <VerificationModal
        isOpen={showVerificationModal}
        onClose={() => setShowVerificationModal(false)}
        onVerify={handleVerification}
        title="Bestätigung erforderlich"
        message="Wir haben dir einen 6-stelligen Code per E-Mail gesendet."
      />
    </div>
  );
};

export default Settings;
